import { useState, useEffect } from 'react';
import { PRESET_TICKERS, TICKERS_STORAGE_KEY } from '../utils/tickers';
import { getValidToken } from '../utils/auth';
import ChartWidget from '../components/ChartWidget';
import CapSeriesChart from '../components/CapSeriesChart';
import SectorSeriesChart from '../components/SectorSeriesChart';
import ContractChartWidget from '../components/ContractChartWidget';
import GenericOptionsWidget from '../components/GenericOptionsWidget';
// import BestContractsWidget from '../components/BestContractsWidget'; // DISABLED 2026-07-10 (SPX Best Cheap Contracts paused)
import SmartEntryWidget from '../components/SmartEntryWidget';
import NewsFeed from '../components/NewsFeed';
// import ContractStatsHistory from '../components/ContractStatsHistory'; // DISABLED 2026-07-10

const API_URL = process.env.REACT_APP_API_URL || '';

// Parallel/shadow copy of the Options page for the WS-powered Smart Entry
// experiment (backend: handlers/smart_entry_ws.go, third Alpaca WebSocket
// connection instead of the original engine's 5s REST poll). Every widget
// here is identical to the Options page except the one Smart Entry
// instance, which points at the WS engine's own route/table/variant - the
// original Options page is untouched and keeps running exactly as before.
export default function OptionsWS() {
  const [tf, setTf] = useState('1m');
  const [chartSymbol, setChartSymbol] = useState('SPX');
  const [customTickers, setCustomTickers] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      let legacy = [];
      try { legacy = JSON.parse(localStorage.getItem(TICKERS_STORAGE_KEY)) || []; }
      catch { legacy = []; }
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/custom-tickers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        let tickers = data.tickers || [];
        const missing = legacy.filter(t => !tickers.includes(t));
        for (const t of missing) {
          const addRes = await fetch(`${API_URL}/api/custom-tickers/add`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ symbol: t }),
          });
          if (addRes.ok) tickers = (await addRes.json()).tickers || tickers;
        }
        if (mounted) setCustomTickers(tickers);
      } catch {
        if (mounted) setCustomTickers(legacy);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const allTickers = [...PRESET_TICKERS, ...customTickers.filter(t => !PRESET_TICKERS.includes(t))];

  const handleCustomTickersChange = async (updated) => {
    setCustomTickers(updated);
    const added = updated.find(t => !customTickers.includes(t));
    if (!added) return;
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/custom-tickers/add`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: added }),
      });
      if (res.ok) setCustomTickers((await res.json()).tickers || updated);
    } catch {
      // stays as the optimistic local update; will reconcile next page load
    }
  };

  const [selectedContract, setSelectedContract] = useState(null);

  return (
    <div>
      <ChartWidget
        tf={tf} setTf={setTf}
        symbol={chartSymbol} setSymbol={setChartSymbol}
        tickers={allTickers}
      />
      <CapSeriesChart tf={tf} setTf={setTf} />
      <SectorSeriesChart tf={tf} setTf={setTf} />
      <ContractChartWidget contract={selectedContract} />
      <GenericOptionsWidget
        onContractSelect={setSelectedContract}
        customTickers={customTickers}
        onCustomTickersChange={handleCustomTickersChange}
      />
      <NewsFeed />
      <SmartEntryWidget
        apiPath="/api/smart-signals-spy-ws"
        title="Smart Entry WS (SPY)"
        subtitle="Same rules as Smart Entry (SPY) - same SL/TP/TS math, same gates, same safeguards - but priced from a live options WebSocket feed instead of a 5s REST poll, with a 1s decision loop instead of 5s. Experimental parallel engine, own table, being validated against real market data."
      />
      {/* DISABLED 2026-07-10 (SPX Best Cheap Contracts paused) */}
      {/* <BestContractsWidget onContractSelect={setSelectedContract} /> */}
      {/* <ContractStatsHistory /> */}
    </div>
  );
}
